
import React, { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { 
  Target, 
  TrendingUp, 
  Clock, 
  BarChart3, 
  Building2, 
  CheckCircle2
} from "lucide-react";
import { Lead } from "./types";

interface LeadMetricsCardsProps {
  leads: Lead[];
}

export const LeadMetricsCards: React.FC<LeadMetricsCardsProps> = ({ leads }) => {
  const metrics = useMemo(() => {
    const totalLeads = leads.length;
    const activeLeads = leads.filter(lead => 
      !['won', 'lost'].includes(lead.status)
    ).length;
    
    const wonLeads = leads.filter(lead => lead.status === 'won').length;
    const conversionRate = totalLeads ? Math.round((wonLeads / totalLeads) * 100) : 0;
    
    const avgScore = totalLeads 
      ? Math.round(leads.reduce((sum, lead) => sum + (lead.lead_score || 0), 0) / totalLeads) 
      : 0;
    
    const highPriorityCount = leads.filter(lead => lead.priority === 'high' || lead.priority === 'urgent').length;
    
    return { 
      totalLeads, 
      activeLeads, 
      wonLeads, 
      conversionRate, 
      avgScore, 
      highPriorityCount 
    };
  }, [leads]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
      <MetricCard 
        title="Total Leads"
        value={metrics.totalLeads}
        icon={<Building2 className="h-4 w-4 text-muted-foreground" />}
        description="All leads in database"
      />
      <MetricCard 
        title="Active Leads"
        value={metrics.activeLeads}
        icon={<Target className="h-4 w-4 text-muted-foreground" />}
        description="Leads in active pipeline"
      />
      <MetricCard 
        title="Won Deals"
        value={metrics.wonLeads}
        icon={<CheckCircle2 className="h-4 w-4 text-muted-foreground" />}
        description="Successfully closed"
      />
      <MetricCard 
        title="Conversion Rate"
        value={`${metrics.conversionRate}%`}
        icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
        description="Lead to won ratio"
      />
      <MetricCard 
        title="Avg. Lead Score"
        value={metrics.avgScore}
        icon={<BarChart3 className="h-4 w-4 text-muted-foreground" />}
        description="Average quality score"
      />
      <MetricCard 
        title="High Priority"
        value={metrics.highPriorityCount}
        icon={<Clock className="h-4 w-4 text-muted-foreground" />}
        description="Urgent attention needed"
      />
    </div>
  );
};

interface MetricCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  description: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, description }) => (
  <Card className="p-4">
    <div className="flex flex-col space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        {icon}
      </div>
      <div className="text-2xl font-bold">{value}</div>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  </Card>
);
